import React, {useEffect, useRef, useState} from 'react';
import {useSelector} from 'react-redux';
import smbUtils from '../../../../utils/smbUtils';
import Header from '../../components/header';
import Loader from '../../../../components/spinner/Loader';
import _get from 'lodash/get';
import {history} from '../../../../utils/redux/store';
import {
  getBrandId,
  getTemplate,
  getTemplateDefaultConfig,
  getTemplateLayout,
  handleImage,
  updateTemplate,
} from '../../../../utils/skincareUtils';
import {
  deviceOptions,
  getAvailableDevices,
  getUiConfigDevice,
  setUiConfigDevice,
} from '../config/utils/optionsUtils';
import {toast} from 'react-toastify';
import {Trans} from 'react-i18next';
import {privilegeUtils} from '../../../../utils/privilegeUtils';
import {Form, Formik} from 'formik';
import ButtonWithRestoreGroup from '../../../../components/formik/ButtonWithRestoreGroup';
import PublishToOnlineModal from '../components/PublishToOnlineModal';
import PreviewModule from '../../components/previewmodule';
import langUtils from '../../../../utils/langUtils';
import {useQuery} from '../../../../components/hook/useQuery';
import skincareProDao from '../../../../dao/skincareProDao';
import langCode from '../../../../utils/langUtils';
import ChooseUITemplate from '../components/ChooseUITemplate';
import GenericLayoutSettings from './components/GenericLayoutSettings';
import CustomizedLayoutSettings from './components/CustomizedLayoutSettings';
import ProcessingInProgressModal from '../components/ProcessingInProgressModal';
import { isFunctionBusyError } from '../../../../utils/errorUtils';

export default function SurveyForm() {
  const auth = useSelector(state => state.components.auth.accountSummary);
  const {token, customerId, brandInfo} = smbUtils.customerInfo(auth);
  const formRef = useRef();

  const [isLoaded, setIsLoaded] = useState(true);
  const [initValue, setInitValue] = useState({});
  const [commonData, setCommonData] = useState({});
  const [templateId, setTemplateId] = useState('');
  const [generalTempInfo, setGeneralTempInfo] = useState({});
  const [customTempDataset, setCustomTempDataset] = useState([]);
  const [selectedTempInfo, setSelectedTempInfo] = useState({});
  const [device, setDevice] = useState(
    getUiConfigDevice(deviceOptions, customerId)
  );
  const [language, setLanguage] = useState(langUtils.getInitialLang());

  const [publishStatus, setPublishStatus] = useState(false);
  const [isShowPublishModal, setIsShowPublishModal] = useState(false);
  const [isShowProcessingModal, setIsShowProcessingModal] = useState(false);
  const templateType = 'SmbSurveyForm';
  const templateType2 = 'SurveyForm';

  const {
    data: templateUrlData,
    isLoading: isLoadingByTemplateUrl,
    refetch: tempPreviewUrlRefetch,
  } = useQuery({
    queryFunc: params => {
      if (!_get(params, 'rvId')) {
        return;
      }
      return _get(params, 'isDefault')
        ? getTemplateLayout(
            token,
            customerId,
            templateType,
            _get(brandInfo, 'templateVersion')
          )
        : getTemplate(token, customerId, _get(params, 'rvId'));
    },
    initData: {indexPage: '', indexFileUrl: ''},
    isInitEnabled: false,
  });

  useEffect(() => {
    if (_get(brandInfo, 'templateVersion') === 1) {
      history.push('/skincare-pro/ui-configuration/landing-page');
    }
    fetchData();
  }, []);

  const updateCustomLang = availableLangs => {
    const availableLangList = langCode.getConsoleStringLangList();
    const matchedLang = availableLangList.find(lang =>
      availableLangs.includes(lang.value)
    );
    setLanguage(matchedLang || availableLangList[0]);
  };

  const updateCustomDevice = availableDevices => {
    setDevice(
      getUiConfigDevice(getAvailableDevices(availableDevices), customerId)
    );
  };

  const fetchData = async () => {
    setIsLoaded(true);
    try {
      const [templateLists] = await Promise.all(
        [
          skincareProDao.listUIConfigTemplates({template_type: templateType2}),
        ].map(promise => promise.catch(() => ({})))
      );
      const templates = _get(templateLists, 'templates', []);
      setCustomTempDataset(templates.filter(template => !template.is_default));

      const defaultItem = templates.find(t => t.is_default);
      if (!defaultItem) {
        toast.error(
          <Trans i18nKey="toast.error.loading.data.failed">
            Failed to load data.
          </Trans>
        );
        return;
      }
      setGeneralTempInfo(defaultItem);
      await getTemplate(
        token,
        customerId,
        _get(defaultItem, 'template_revision_id')
      ).then(res => {
        if (res && res.configJson) {
          setInitValue({
            ..._get(JSON.parse(res.configJson), 'page[0]'),
            is_default: _get(res, 'is_default', true),
            template_id: _get(res, 'templateId', ''),
            template_revision_id: _get(
              defaultItem,
              'template_revision_id',
              null
            ),
          });
        }
        setTemplateId(_get(res, 'templateId', ''));
      });

      //For general template post massage
      await getBrandId(token, customerId).then(async res => {
        if (res && res.commonSettingRvId) {
          await getTemplate(
            token,
            customerId,
            _get(res, 'commonSettingRvId'),
            'CommonSetting'
          ).then(res => {
            if (res && res.configJson) {
              setCommonData(JSON.parse(res.configJson));
            }
          });
        }
      });

      const selectedItem = templates.find(t => t.selected);
      if (!selectedItem) {
        toast.error(
          <Trans i18nKey="toast.error.loading.data.failed">
            Failed to load data.
          </Trans>
        );
        return;
      }

      if (selectedItem.is_default) {
        setSelectedTempInfo({
          ...selectedItem,
          device_type: deviceOptions.map(opt => opt.value),
          langs: langCode.getConsoleStringLangList().map(opt => opt.value),
        });
      } else {
        setSelectedTempInfo(selectedItem);
        updateCustomLang(_get(selectedItem, 'langs', []));
        updateCustomDevice(_get(selectedItem, 'device_type', []));
      }

      await tempPreviewUrlRefetch({
        isDefault: selectedItem.is_default,
        rvId: selectedItem.template_revision_id,
      });
    } catch (error) {
      toast.error(
        <Trans i18nKey="toast.error.loading.data.failed">
          Failed to load data.
        </Trans>
      );
    } finally {
      setIsLoaded(false);
    }
  };

  const handleRestore = async () => {
    getTemplateDefaultConfig(templateType, '', 2, customerId).then(r => {
      if (r && r.templateMetadata && r.pageConfig) {
        updateTemplate(
          token,
          customerId,
          templateId,
          templateType2,
          r.templateMetadata,
          r.fileId,
          r.pageConfig
        ).then(async r => {
          setPublishStatus(!publishStatus);
          setIsLoaded(true);
          await fetchData();
          toast.success(
            <Trans i18nKey="c.f.simple.button.group.success">success</Trans>
          );
        });
      }
    });
  };

  const handleSelectedTemplate = async (formikProps, targetItem) => {
    formikProps.setFieldValue(
      'is_default',
      _get(targetItem, 'is_default', true)
    );
    formikProps.setFieldValue(
      'template_id',
      _get(targetItem, 'template_id', null)
    );
    formikProps.setFieldValue(
      'template_revision_id',
      _get(targetItem, 'template_revision_id', null)
    );
    setSelectedTempInfo(targetItem);
    updateCustomLang(_get(targetItem, 'langs', []));
    updateCustomDevice(_get(targetItem, 'device_type', []));
    await tempPreviewUrlRefetch({
      isDefault: targetItem.is_default,
      rvId: targetItem.template_revision_id,
    });
  };

  const formikConfig = {
    enableReinitialize: true,
    initialValues: getInitValue(initValue),
    onSubmit: async (values, {setSubmitting}) => {
      setSubmitting(true);
      if (
        !privilegeUtils.canCustomizationAppLayout(auth) &&
        !values.is_default
      ) {
        toast.error(
          <Trans i18nKey="toast.error.customized.version.failed">
            If you need to set up a customized template, please go to Help >
            Contact Us.
          </Trans>
        );
        setSubmitting(false);
        return;
      }

      if (!values.is_default && !values.template_id) {
        toast.error(
          <Trans i18nKey="toast.error.update.template.failed">
            Failed to update, please choose a template.
          </Trans>
        );
        setSubmitting(false);
        return;
      }

      if (!values.is_default) {
        await skincareProDao
          .selectUIConfigTemplate({
            ...selectedTempInfo,
            template_type: templateType2,
          })
          .then(r => {
            setIsShowPublishModal(true);
          })
          .catch(r => {
            toast.error(
              <Trans i18nKey="c.f.simple.button.group.failed">failed</Trans>
            );
            setSubmitting(false);
          });
        return;
      }

      async function ImageToUrl(name) {
        if (_get(values, `${name}.image`)) {
          return await handleImage(
            _get(values, `${name}.image`),
            token,
            customerId
          );
        } else if (_get(values, `${name}.url`)) {
          return await _get(values, `${name}.url`);
        } else if (smbUtils.isUrl(_get(values, `${name}`))) {
          return await _get(values, `${name}`);
        }
        return '';
      }

      const padImages = [
        {
          file_url: await ImageToUrl('mainImage[0].imagesUrl[0]'),
          path: './assets/images/banner_pad_1.jpg',
        },
        {
          file_url: '',
          path: '',
        },
        {
          file_url: '',
          path: '',
        },
      ];
      const phoneImages = [
        {
          file_url: await ImageToUrl('mainImage[1].imagesUrl[0]'),
          path: './assets/images/banner_phone_1.jpg',
        },
        {
          file_url: '',
          path: '',
        },
        {
          file_url: '',
          path: '',
        },
      ];
      const landscapeImages = [
        {
          file_url: await ImageToUrl('mainImage[2].imagesUrl[0]'),
          path: './assets/images/banner_pad_landscape_1.jpg',
        },
        {
          file_url: '',
          path: '',
        },
        {
          file_url: '',
          path: '',
        },
      ];
      const data = {
        page: [
          {
            templateType: 'skincare',
            pageType: 'surveyForm',
            ...values,
            mainImage: [
              {
                ...values.mainImage[0],
                deviceType: 'pad',
                images:
                  padImages.filter(r => r.file_url !== '').map(rr => rr.path) ||
                  [],
                imagesUrl:
                  padImages
                    .filter(r => r.file_url !== '')
                    .map(rr => rr.file_url) || [],
                autoplayTimeout: 60000,
              },
              {
                ...values.mainImage[1],
                deviceType: 'phone',
                images:
                  phoneImages
                    .filter(r => r.file_url !== '')
                    .map(rr => rr.path) || [],
                imagesUrl:
                  phoneImages
                    .filter(r => r.file_url !== '')
                    .map(rr => rr.file_url) || [],
                autoplayTimeout: 60000,
              },
              {
                ...values.mainImage[2],
                deviceType: 'padLandscape',
                images:
                  landscapeImages
                    .filter(r => r.file_url !== '')
                    .map(rr => rr.path) || [],
                imagesUrl:
                  landscapeImages
                    .filter(r => r.file_url !== '')
                    .map(rr => rr.file_url) || [],
                autoplayTimeout: 60000,
              },
            ],
          },
        ],
      };

      await updateTemplate(
        token,
        customerId,
        templateId,
        templateType2,
        {
          files: [
            ...padImages.filter(r => r.file_url !== ''),
            ...phoneImages.filter(r => r.file_url !== ''),
            ...landscapeImages.filter(r => r.file_url !== ''),
          ],
        },
        '',
        data
      )
        .then(async () => {
          if (privilegeUtils.canCustomizationAppLayout(auth)) {
            try {
              await skincareProDao.selectUIConfigTemplate({
                ...selectedTempInfo,
                template_type: templateType2,
              });
            } catch (err) {
              toast.error(
                <Trans i18nKey="c.f.simple.button.group.failed">failed</Trans>
              );
            }
          }

          setIsShowPublishModal(true);
        })
        .catch(e => {
          if (isFunctionBusyError(e)) {
            setIsShowProcessingModal(true);
            setSubmitting(false);
            return;
          }

          toast.error(
            <Trans i18nKey="c.f.simple.button.group.failed">failed</Trans>
          );
          setSubmitting(false);
        });
    },
  };

  if (
    !(
      privilegeUtils.canUiConfig(auth) ||
      privilegeUtils.isTechnicalSupport(auth)
    )
  ) {
    history.push('/');
  }

  return isLoaded ? (
    <div>
      <Header />
      <Loader />
    </div>
  ) : (
    <Formik {...formikConfig} ref={formRef}>
      {formikProps => (
        <Form className="skincare-configuration-form">
          <Header publishStatus={publishStatus} dirty={formikProps.dirty} />
          <div className="skincare-configuration-layout">
            <div className="skincare-configuration-layout__left">
              <div className="kt-portlet skincare-configuration-layout__left-portlet">
                <div className="kt-portlet__body pb-0">
                  <ChooseUITemplate
                    isDefaultLayout={_get(selectedTempInfo, 'is_default')}
                    setIsDefaultLayout={v => {
                      let targetItem = customTempDataset.find(
                        item => item.is_default === false
                      );
                      if (!targetItem) {
                        targetItem = {
                          is_default: v,
                          template_id: null,
                          template_revision_id: null,
                          device_type: deviceOptions.map(op => op.value),
                          langs: langCode
                            .getConsoleStringLangList()
                            .map(op => op.value),
                        };
                      }
                      if (v) {
                        targetItem = {
                          ...generalTempInfo,
                          is_default: true,
                          device_type: deviceOptions.map(op => op.value),
                          langs: langCode
                            .getConsoleStringLangList()
                            .map(op => op.value),
                        };
                      }
                      handleSelectedTemplate(formikProps, targetItem);
                    }}
                  />
                </div>
                <div className="kt-portlet__body skincare-configuration-layout__left-portlet__body">
                  {_get(selectedTempInfo, 'is_default') && (
                    <GenericLayoutSettings
                      formikProps={formikProps}
                      device={device}
                      setDevice={setDevice}
                    />
                  )}
                  {!_get(selectedTempInfo, 'is_default') && (
                    <CustomizedLayoutSettings
                      data={customTempDataset}
                      isLoading={isLoaded}
                      selectedTempInfo={selectedTempInfo}
                      handleSelectedTemplate={targetItem =>
                        handleSelectedTemplate(formikProps, targetItem)
                      }
                    />
                  )}
                </div>
                <div className="d-flex justify-content-end kt-margin-t-20">
                  <ButtonWithRestoreGroup
                    saveDirty={formikProps.dirty}
                    discardButton={false}
                    isUpdating={true}
                    onRestoreClick={() => {
                      handleRestore().then(() => formikProps.resetForm());
                    }}
                  />
                </div>
              </div>
            </div>
            {!isLoadingByTemplateUrl && formikProps.values && (
              <div className="skincare-configuration-layout__right">
                <PreviewModule
                  values={formikProps.values}
                  template={{
                    page: {
                      page: [
                        {
                          ...formikProps.values,
                          mainImage: [
                            {
                              ...formikProps.values.mainImage[0],
                              images: [
                                _get(
                                  formikProps.values,
                                  'mainImage[0].imagesUrl[0].image'
                                ),
                                '',
                                '',
                              ],
                              autoplayTimeout: 60000,
                            },
                            {
                              ...formikProps.values.mainImage[1],
                              images: [
                                _get(
                                  formikProps.values,
                                  'mainImage[1].imagesUrl[0].image'
                                ),
                                '',
                                '',
                              ],
                              autoplayTimeout: 60000,
                            },
                            {
                              ...formikProps.values.mainImage[2],
                              images: [
                                _get(
                                  formikProps.values,
                                  'mainImage[2].imagesUrl[0].image'
                                ),
                                '',
                                '',
                              ],
                              autoplayTimeout: 60000,
                            },
                          ],
                        },
                      ],
                    },
                    common: {
                      ...commonData,
                      page: [
                        {
                          ..._get(commonData, 'page[0]'),
                          header: {
                            logo: {
                              image: _get(
                                commonData,
                                'page[0].header.logo.imageUrl',
                                ''
                              ),
                            },
                            displayName: _get(
                              commonData,
                              'page[0].header.displayName',
                              'showAll'
                            ),
                          },
                        },
                      ],
                    },
                  }}
                  previewUrl={
                    selectedTempInfo.is_default
                      ? _get(templateUrlData, 'indexPage')
                      : _get(templateUrlData, 'indexFileUrl')
                  }
                  lang={language}
                  type="surveyForm"
                  deviceList={getAvailableDevices(
                    deviceOptions,
                    _get(selectedTempInfo, 'device_type', [])
                  )}
                  langList={langUtils.getAvailableConsoleStringLangList(
                    _get(selectedTempInfo, 'langs', [])
                  )}
                  onChangeDevice={device =>
                    setDevice(setUiConfigDevice(device))
                  }
                  isCustomized={!_get(selectedTempInfo, 'is_default', false)}
                />
              </div>
            )}
          </div>
          <PublishToOnlineModal
            isShow={isShowPublishModal}
            onPublish={isPublish => {
              formikProps.setSubmitting(false);
              formikProps.resetForm(formikProps.values);
              setPublishStatus(isPublish);
              setIsShowPublishModal(false);
            }}
          />
          <ProcessingInProgressModal
            show={isShowProcessingModal}
            onHide={() => {
              setIsShowProcessingModal(false);
            }}
          />
        </Form>
      )}
    </Formik>
  );
}

const getInitValue = values => {
  return {
    ...values,
    mainImage: [
      {
        ..._get(values, 'mainImage[0]'),
        imagesUrl: [
          {
            url: _get(values, 'mainImage[0].imagesUrl[0]', ''),
            image: _get(values, 'mainImage[0].imagesUrl[0]', ''),
          },
          {
            url: '',
            image: '',
          },
          {
            url: '',
            image: '',
          },
        ],
        autoplayTimeout: 60000,
      },
      {
        ..._get(values, 'mainImage[1]'),
        imagesUrl: [
          {
            url: _get(values, 'mainImage[1].imagesUrl[0]', ''),
            image: _get(values, 'mainImage[1].imagesUrl[0]', ''),
          },
          {
            url: '',
            image: '',
          },
          {
            url: '',
            image: '',
          },
        ],
        autoplayTimeout: 60000,
      },
      {
        ..._get(values, 'mainImage[2]'),
        imagesUrl: [
          {
            url: _get(values, 'mainImage[2].imagesUrl[0]', ''),
            image: _get(values, 'mainImage[2].imagesUrl[0]', ''),
          },
          {
            url: '',
            image: '',
          },
          {
            url: '',
            image: '',
          },
        ],
        autoplayTimeout: 60000,
      },
    ],
  };
};
